import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTimeout} from '@/app/hooks/useTimers';
import {Notice} from '@/components/app/feedback';
import {InfoSection} from '../../../../app/components/InfoSection';
import {useLazyGetSubscriptionStatusQuery} from '../../../../app/services/subscriptionApi';
import {SubscriptionStatus} from '../../../auth/types';
import {SuccessComponentProps} from '../../type';
import {useLazyGetBiotwinSharesQuery} from '../../../../app/services/biotwinShareApi';
import {useRefreshTokenMutation} from '../../../../app/services/identitiesApi';
import {logout} from '../../../auth/authSlice';
export const SuccessSubscribe = (props: SuccessComponentProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inProgress, setInProgress] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [getSubscriptionStatus] = useLazyGetSubscriptionStatusQuery();
  const [getBiotwinShares, {isLoading: isLoadingGetBiotwinShares}] = useLazyGetBiotwinSharesQuery();
  const [refreshToken, {isLoading: isLoadingRefreshToken}] = useRefreshTokenMutation();
  useTimeout(() => {
    setMinTimePassed(true);
  }, 1000);
  useEffect(() => {
    let retryCount = 0;
    const maxRetryCount = 5;
    const abortController = new AbortController();
    const signal = abortController.signal;
    const check = async (): Promise<unknown> => {
      if (signal.aborted) {
        return;
      }
      retryCount++;
      let status: SubscriptionStatus | null = null;
      try {
        status = await getSubscriptionStatus().unwrap();
      } catch (e) {}
      if (!signal.aborted) {
        if (retryCount > maxRetryCount) {
          setHasError(true);
          setInProgress(false);
        } else if (status === SubscriptionStatus.Success) {
          setInProgress(false);
        } else if (status === SubscriptionStatus.Error) {
          setInProgress(false);
        } else {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return check();
        }
      }
    };
    check();
    return () => {
      abortController.abort();
    };
  }, [getSubscriptionStatus]);
  return inProgress || !minTimePassed ? renderInProgress() : hasError ? renderError() : renderSuccess();
  function renderError() {
    return <InfoSection {...props} icon="exclamation-circle" message="???" title="Oops! Something went wrong." />;
  }
  function renderInProgress() {
    return (
      <InfoSection
        {...props}
        content={
          <div>
            <Notice tone="info" text="Do not refresh the page or navigate away while this is happening." />
            <p>Thank you for your patience!</p>
          </div>
        }
        icon="spinner"
        iconClassName="animate-spin"
        message="We are processing your payment."
        title="Please wait..."
      />
    );
  }
  function renderSuccess() {
    return (
      <InfoSection
        {...props}
        actions={[
          {
            props: {
              children: 'Start Exploring Biotandem',
              isLoading: isLoadingRefreshToken || isLoadingGetBiotwinShares,
              onClick: () => {
                refreshToken()
                  .unwrap()
                  .then(() => {
                    getBiotwinShares()
                      .unwrap()
                      .then((biotwinShares) => navigate(biotwinShares.length === 0 ? '/' : '/confirm-biotwin-share'))
                      .catch(() => navigate('/'));
                  })
                  .catch(() => {
                    navigate('/sign-in');
                    dispatch(logout());
                  });
              },
            },
            type: 'button',
          },
        ]}
        message="You are successfully subscribed."
        title="Congratulations!"
      />
    );
  }
};
